import * as React from "react"
import {
  Bell,
  BellOff,
  LogOut,
  MessageSquarePlus,
  Plus,
  Search,
  X,
  Volume2,
} from "lucide-react"
import { Link, useMatch, useNavigate } from "react-router"

import { ConnectionBanner } from "@/components/chat/connection-banner"
import { UserBlobatar } from "@/components/chat/blobatar"
import { UserAvatar } from "@/components/chat/user-avatar"
import { UserPicker } from "@/components/chat/user-picker"
import { Button } from "@/components/ui/button"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { formatListTime, previewText, roomDisplayName } from "@/chat/format"
import type { RoomSummary, User } from "@/chat/protocol"
import { useChat } from "@/chat/store"
import { cn } from "@/lib/utils"

type Composer = "none" | "room" | "direct"

/**
 * The chat list: header, search, the two "new" forms and the rows. Fills
 * whatever it is placed in — the whole screen on a phone, the sidebar of the
 * `Workspace` on desktop.
 */
/**
 * What the `local` state means for the person: the browser's push service is
 * out of reach (Google's for Chrome, Apple's for Safari), so the app can only
 * notify while it is running.
 */
const LOCAL_ONLY_NOTE =
  "Уведомления придут, пока приложение открыто (можно свернуть). Push-сервис браузера недоступен, поэтому при закрытом приложении их не будет."

export function RoomsPane() {
  const {
    rooms,
    unreadRooms,
    signOut,
    user,
    self,
    pushState,
    setPushEnabled,
    voice,
  } = useChat()
  const activeId = useMatch("/r/:id/*")?.params.id ?? null
  const [pushError, setPushError] = React.useState<string | null>(null)
  const [pushNote, setPushNote] = React.useState<string | null>(null)
  const notifying = pushState === "on" || pushState === "local"

  async function togglePush() {
    setPushError(null)
    setPushNote(null)
    try {
      const next = await setPushEnabled(!notifying)
      // Said once, when it happens; the switch's title keeps saying it.
      if (next === "local") setPushNote(LOCAL_ONLY_NOTE)
    } catch (caught) {
      setPushError(
        caught instanceof Error
          ? caught.message
          : "Не удалось включить уведомления."
      )
    }
  }
  const [query, setQuery] = React.useState("")
  const [composer, setComposer] = React.useState<Composer>("none")

  const selfId = user?.id ?? null

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return rooms

    return rooms.filter(
      (room) =>
        roomDisplayName(room, selfId).toLowerCase().includes(needle) ||
        room.topic.toLowerCase().includes(needle) ||
        (room.lastMessage?.text.toLowerCase().includes(needle) ?? false)
    )
  }, [rooms, query, selfId])

  function toggle(target: Composer) {
    setComposer((current) => (current === target ? "none" : target))
  }

  return (
    <>
      <header className="flex-none px-4 pt-3 pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold tracking-tight">
              Чаты
            </h1>
            <button
              type="button"
              onClick={signOut}
              title="Выйти из аккаунта"
              className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {user?.name ?? "…"}
              <span aria-hidden>·</span>
              <LogOut className="size-3" aria-hidden />
              выйти
            </button>
          </div>

          <div className="flex gap-1.5">
            {pushState !== "unsupported" && (
              <Button
                variant={notifying ? "secondary" : "outline"}
                size="icon"
                aria-pressed={notifying}
                aria-label={
                  notifying ? "Выключить уведомления" : "Включить уведомления"
                }
                title={
                  pushState === "denied"
                    ? "Уведомления запрещены в настройках браузера"
                    : pushState === "on"
                      ? "Уведомления включены"
                      : pushState === "local"
                        ? LOCAL_ONLY_NOTE
                        : "Уведомлять о новых сообщениях"
                }
                disabled={pushState === "denied" || pushState === "busy"}
                onClick={() => void togglePush()}
              >
                {pushState === "busy" ? (
                  <Spinner className="size-4" />
                ) : notifying ? (
                  <Bell className="size-4" />
                ) : (
                  <BellOff className="size-4" />
                )}
              </Button>
            )}

            <Button
              variant={composer === "direct" ? "secondary" : "outline"}
              size="icon"
              aria-label={
                composer === "direct" ? "Отменить" : "Написать пользователю"
              }
              onClick={() => toggle("direct")}
            >
              {composer === "direct" ? (
                <X className="size-4" />
              ) : (
                <MessageSquarePlus className="size-4" />
              )}
            </Button>

            <Button
              variant={composer === "room" ? "secondary" : "outline"}
              size="icon"
              aria-label={composer === "room" ? "Отменить" : "Новая комната"}
              onClick={() => toggle("room")}
            >
              {composer === "room" ? (
                <X className="size-4" />
              ) : (
                <Plus className="size-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="relative mt-3">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Найти чат"
            aria-label="Поиск по чатам"
            className="pl-9"
          />
        </div>
      </header>

      <ConnectionBanner />

      {pushError && (
        <p role="alert" className="px-4 pb-2 text-xs text-destructive">
          {pushError}
        </p>
      )}

      {pushNote && (
        <p role="status" className="px-4 pb-2 text-xs text-muted-foreground">
          {pushNote}
        </p>
      )}

      {composer === "room" && (
        <NewRoomForm onDone={() => setComposer("none")} />
      )}
      {composer === "direct" && <NewDirectForm />}

      <div className="no-scrollbar flex-1 overflow-y-auto overscroll-contain px-2 pb-4">
        {filtered.length === 0 ? (
          <Empty>
            <EmptyHeader>
              {self === null ? (
                // No `welcome` yet: the list is unknown, not empty.
                <>
                  <Spinner />
                  <EmptyTitle>Загрузка чатов</EmptyTitle>
                </>
              ) : rooms.length === 0 ? (
                <>
                  <EmptyTitle>Пока нет чатов</EmptyTitle>
                  <EmptyDescription>
                    Создайте комнату или напишите кому-нибудь — кнопки сверху.
                  </EmptyDescription>
                </>
              ) : (
                <>
                  <EmptyTitle>Нет совпадений</EmptyTitle>
                  <EmptyDescription>
                    Ничего не нашлось по запросу “{query}”.
                  </EmptyDescription>
                </>
              )}
            </EmptyHeader>
          </Empty>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((room) => (
              <li key={room.id}>
                <RoomRow
                  room={room}
                  selfId={selfId}
                  unread={unreadRooms[room.id] ?? false}
                  active={room.id === activeId}
                  inVoice={voice[room.id]?.length ?? 0}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

/**
 * A new group: a name and the people to start it with. The creator is added
 * by the server; anyone else can be invited later from the room's info page.
 */
function NewRoomForm({ onDone }: { onDone: () => void }) {
  const { createRoom, user } = useChat()
  const [name, setName] = React.useState("")
  const [members, setMembers] = React.useState<User[]>([])

  const excluded = React.useMemo(
    () => new Set([user?.id ?? "", ...members.map((m) => m.id)]),
    [user, members]
  )

  function submit(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return

    createRoom(
      trimmed,
      members.map((m) => m.id)
    )
    setName("")
    setMembers([])
    onDone()
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-none flex-col gap-2 border-b px-4 pb-3"
    >
      <div className="flex gap-2">
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Имя комнаты"
          aria-label="Имя новой комнаты"
          autoFocus
          maxLength={40}
        />
        <Button type="submit" disabled={!name.trim()}>
          Создать
        </Button>
      </div>

      {members.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Участники">
          {members.map((member) => (
            <li key={member.id}>
              <button
                type="button"
                onClick={() =>
                  setMembers((current) =>
                    current.filter((m) => m.id !== member.id)
                  )
                }
                aria-label={`Убрать ${member.name}`}
                className="flex items-center gap-1 rounded-full bg-secondary py-0.5 pr-1.5 pl-2 text-xs hover:bg-accent"
              >
                {member.name}
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <UserPicker
        placeholder="Добавить участника по логину"
        exclude={excluded}
        onPick={(person) => setMembers((current) => [...current, person])}
      />
    </form>
  )
}

/**
 * Finds a person by login prefix and opens (or creates) the direct room with
 * them. The room arrives as a `direct` frame; navigation waits for it so the
 * thread never opens on an id the room list does not know yet.
 */
function NewDirectForm() {
  const { openDirect } = useChat()
  const navigate = useNavigate()
  const [opening, setOpening] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  async function pick(target: User) {
    if (opening) return

    setOpening(target.id)
    setError(null)

    try {
      const room = await openDirect(target.id)
      navigate(`/r/${room.id}`)
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Не удалось открыть чат."
      )
      setOpening(null)
    }
  }

  return (
    <div className="flex flex-none flex-col gap-2 border-b px-4 pb-3">
      <UserPicker
        placeholder="Логин собеседника"
        busyId={opening}
        onPick={(person) => void pick(person)}
        autoFocus
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function RoomRow({
  room,
  selfId,
  unread,
  active,
  inVoice,
}: {
  room: RoomSummary
  selfId: string | null
  unread: boolean
  /** The room open in the other pane; only ever true on desktop. */
  active: boolean
  /** How many people are in the room's voice chat. */
  inVoice: number
}) {
  const title = roomDisplayName(room, selfId)
  const last = room.lastMessage

  // In a direct room the author is either you or the person named in the
  // title, so the byline is only informative for your own side.
  const preview = last
    ? room.kind === "direct"
      ? last.userId === selfId
        ? `Вы: ${previewText(last)}`
        : previewText(last)
      : `${last.name}: ${previewText(last)}`
    : room.topic || "Сообщений пока нет"

  return (
    <Link
      to={`/r/${room.id}`}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-accent",
        active && "bg-accent"
      )}
    >
      {room.kind === "direct" ? (
        <UserBlobatar name={title} className="size-10" />
      ) : (
        <UserAvatar name={title} className="size-10" accent={unread} />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm",
              unread ? "font-semibold" : "font-medium"
            )}
          >
            {title}
          </span>
          {last && (
            <span className="flex-none text-xs text-muted-foreground">
              {formatListTime(last.at)}
            </span>
          )}
        </div>

        <p
          className={cn(
            "mt-0.5 truncate text-xs",
            unread ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {preview}
        </p>

        {inVoice > 0 && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-primary">
            <Volume2 className="size-3" />
            Голосовой чат: {inVoice}
          </p>
        )}

        {room.kind === "group" && room.memberCount > 0 && (
          <p className="mt-0.5 truncate text-[11px] text-primary">
            Сейчас здесь: {room.memberCount}
          </p>
        )}
      </div>

      {unread && (
        <span
          aria-label="Непрочитанные сообщения"
          className="size-2 flex-none rounded-full bg-primary"
        />
      )}
    </Link>
  )
}
